/**
 * MediaSelector — Modal minimal pour selectionner 1 ou N medias.
 * Utilise GET /api/media?type=... (dual-read: fallback photos si media absent)
 * Props : { open, onOpenChange, onSelect, multiple, type }
 */
import { useEffect, useState } from "react";
import { getMediaList, rotateMedia } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Check, Image as ImageIcon, Video as VideoIcon, RotateCcw, RotateCw } from "lucide-react";
import { toast } from "sonner";

export default function MediaSelector({ open, onOpenChange, onSelect, multiple = false, type = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [rotating, setRotating] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setLoading(true);
    const params = { limit: 200 };
    if (type) params.type = type;
    getMediaList(params)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, type]);

  const handleRotate = async (e, media, direction) => {
    e.stopPropagation();
    setRotating(media.id);
    try {
      const res = await rotateMedia(media.id, { direction });
      // Mise a jour locale (cache-bust via timestamp)
      setItems((prev) => prev.map((m) => m.id === media.id ? { ...m, ...res.data, __ts: Date.now() } : m));
      toast.success(`Rotation ${direction === "cw" ? "↷" : "↶"} 90° appliquée`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur rotation");
    } finally {
      setRotating(null);
    }
  };

  const filtered = search
    ? items.filter((m) => (m.id + " " + (m.album_id || "") + " " + JSON.stringify(m.captions || {})).toLowerCase().includes(search.toLowerCase()))
    : items;

  const toggle = (m) => {
    if (multiple) {
      setSelected((s) => (s.find((x) => x.id === m.id) ? s.filter((x) => x.id !== m.id) : [...s, m]));
    } else {
      onSelect(m);
      onOpenChange(false);
    }
  };

  const confirm = () => {
    onSelect(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="media-selector">
        <DialogHeader>
          <DialogTitle>Sélectionner {multiple ? "des médias" : "un média"}{type ? ` (${type})` : ""}</DialogTitle>
        </DialogHeader>
        <div className="py-3">
          <Input placeholder="Rechercher par id, album…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="media-selector-search" />
        </div>
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "70vh" }}
        >
          <div
            className="grid gap-3 p-1"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}
            data-testid="media-selector-grid"
          >
          {loading ? (
            <div className="col-span-full text-center text-muted-foreground text-sm py-8">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="col-span-full text-center text-muted-foreground text-sm py-8">Aucun média</div>
          ) : (
            filtered.map((m) => {
              const isSelected = selected.find((x) => x.id === m.id);
              const isVideo = m.type === "video";
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m)}
                  className={`relative w-full rounded-md overflow-hidden border-2 transition-all bg-secondary/30 group ${
                    isSelected ? "border-accent ring-2 ring-accent/40" : "border-transparent hover:border-primary/60"
                  }`}
                  style={{ aspectRatio: "1 / 1" }}
                  data-testid={`media-item-${m.id}`}
                >
                  {isVideo ? (
                    m.poster_url ? (
                      <img src={m.poster_url} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    ) : (
                      <div className="w-full h-full bg-primary/10 flex items-center justify-center"><VideoIcon className="h-8 w-8 text-primary/60" /></div>
                    )
                  ) : (
                    m.public_cloudinary_url || m.original_cloudinary_url ? (
                      <img
                        src={(m.public_cloudinary_url || m.original_cloudinary_url) + (m.__ts ? `#${m.__ts}` : "")}
                        alt=""
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <div className="w-full h-full bg-secondary flex items-center justify-center"><ImageIcon className="h-8 w-8 text-primary/30" /></div>
                    )
                  )}
                  {isVideo && (
                    <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1 rounded flex items-center gap-1">
                      <VideoIcon className="h-3 w-3" /> vidéo
                    </span>
                  )}
                  {!isVideo && (
                    <div className="absolute bottom-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => handleRotate(e, m, "ccw")}
                        disabled={rotating === m.id}
                        className="bg-black/70 hover:bg-black text-white rounded p-1 disabled:opacity-40"
                        title="Rotation 90° gauche"
                        data-testid={`media-${m.id}-rotate-ccw`}
                      >
                        <RotateCcw className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleRotate(e, m, "cw")}
                        disabled={rotating === m.id}
                        className="bg-black/70 hover:bg-black text-white rounded p-1 disabled:opacity-40"
                        title="Rotation 90° droite"
                        data-testid={`media-${m.id}-rotate-cw`}
                      >
                        <RotateCw className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {isSelected && (
                    <span className="absolute top-1 right-1 bg-accent text-white rounded-full p-1 shadow-md">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </button>
              );
            })
          )}
          </div>
        </div>
        {multiple && (
          <DialogFooter>
            <div className="flex-1 text-sm text-muted-foreground">{selected.length} sélectionné(s)</div>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={confirm} disabled={selected.length === 0} data-testid="media-selector-confirm">
              Valider ({selected.length})
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
